// src/Components/Modules/CRM/CrmLeads.js
import React, { useState, useEffect } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box, Typography, Paper, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, FormControl, InputLabel, Stack, Avatar, Divider, Skeleton
} from "@mui/material";
import { FaPlus, FaPhone, FaHandshake, FaEnvelope, FaStickyNote, FaCheckCircle, FaCalendarAlt } from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

const TimelineItem = styled(Paper)(({ theme, typeColor }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  background: "white",
  borderLeft: `4px solid ${typeColor}`,
  marginBottom: theme.spacing(1.5),
  display: "flex",
  gap: theme.spacing(2),
}));

const typeConfig = {
  call: { icon: <FaPhone />, color: "#3b82f6", label: "Call" },
  meeting: { icon: <FaHandshake />, color: "#8b5cf6", label: "Meeting" },
  email: { icon: <FaEnvelope />, color: "#10b981", label: "Email" },
  note: { icon: <FaStickyNote />, color: "#f59e0b", label: "Note" },
  task: { icon: <FaCheckCircle />, color: "#06b6d4", label: "Task" },
  other: { icon: <FaStickyNote />, color: "#6b7280", label: "Other" },
};

const CrmActivities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ type: "note", subject: "", description: "", scheduled_at: "" });

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const res = await AxiosAuthInstance.get("crm/activities");
      setActivities(res.data.data || []);
    } catch (err) {
      Swal.fire("Error", "Failed to load activities", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.subject.trim()) {
      Swal.fire("Warning", "Subject is required", "warning");
      return;
    }
    try {
      await AxiosAuthInstance.post("crm/activities", form);
      Swal.fire({ icon: "success", title: "Added!", timer: 1500, showConfirmButton: false });
      setDialogOpen(false);
      setForm({ type: "note", subject: "", description: "", scheduled_at: "" });
      fetchActivities();
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Operation failed", "error");
    }
  };

  const handleComplete = async (id) => {
    try {
      await AxiosAuthInstance.post(`crm/activities/${id}/complete`);
      fetchActivities();
    } catch (err) {
      Swal.fire("Error", "Failed to complete", "error");
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="CRM Activities" />

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h5" fontWeight={700}><FaCalendarAlt style={{ marginRight: 10 }} />Activity Timeline</Typography>
        <Button variant="contained" startIcon={<FaPlus />} onClick={() => setDialogOpen(true)} sx={{ bgcolor: "#667eea", textTransform: "none" }}>Log Activity</Button>
      </Paper>

      {loading ? (
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      ) : activities.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: "center" }}><Typography color="text.secondary">No activities logged yet</Typography></Paper>
      ) : (
        <Stack>
          {activities.map((activity) => {
            const config = typeConfig[activity.type] || typeConfig.other;
            return (
              <TimelineItem key={activity.id} typeColor={config.color} elevation={0}>
                <Avatar sx={{ bgcolor: config.color, width: 40, height: 40 }}>{config.icon}</Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Typography variant="subtitle1" fontWeight={600}>{activity.subject}</Typography>
                    <Chip label={config.label} size="small" sx={{ bgcolor: `${config.color}15`, color: config.color }} />
                  </Box>
                  {activity.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{activity.description}</Typography>}
                  <Box sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}>
                    <Typography variant="caption" color="text.secondary">By: {activity.user?.name || "System"}</Typography>
                    {activity.scheduled_at && <Typography variant="caption" color="text.secondary">Scheduled: {new Date(activity.scheduled_at).toLocaleString()}</Typography>}
                    {activity.completed_at && <Typography variant="caption" color="success.main">Completed: {new Date(activity.completed_at).toLocaleString()}</Typography>}
                  </Box>
                </Box>
                {activity.status === "scheduled" && (
                  <Button size="small" variant="outlined" onClick={() => handleComplete(activity.id)} sx={{ textTransform: "none" }}>Mark Complete</Button>
                )}
              </TimelineItem>
            );
          })}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#667eea", color: "white" }}>Log New Activity</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Type *</InputLabel>
              <Select value={form.type} label="Type *" onChange={(e) => setForm({ ...form, type: e.target.value })}>
                {Object.entries(typeConfig).map(([key, cfg]) => <MenuItem key={key} value={key}>{cfg.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth label="Subject *" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
            <TextField fullWidth label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} multiline rows={3} />
            <TextField fullWidth label="Scheduled At" type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} InputLabelProps={{ shrink: true }} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} sx={{ bgcolor: "#667eea" }}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrmActivities;